import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useLanguage, useTheme } from "../App";
import { Button } from "../components/ui/button";
import { Check, X, Calendar, Clock, User, MapPin, Sun, Moon } from "lucide-react";

const ConfirmAppointment = () => {
  const { token } = useParams();
  const { t, language } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Don't auto-confirm, just load page
    setLoading(false);
  }, []);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const response = await axios.get(`${API}/public/confirm/${token}`);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || (language === "it" ? "Errore nella conferma" : "Confirmation error"));
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-glow">
          <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-accent"></div>
          </div>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full glass-card rounded-2xl p-8 text-center animate-fade-in">
          <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-serif font-medium mb-2">
            {language === "it" ? "Appuntamento Confermato!" : "Appointment Confirmed!"}
          </h1>
          <p className="text-muted-foreground mb-6">
            {language === "it" 
              ? "Grazie per aver confermato. Ti aspettiamo!"
              : "Thank you for confirming. We'll see you soon!"
            }
          </p>
          
          <div className="bg-muted/50 rounded-xl p-4 text-left space-y-3 mb-6">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t("date")}</p>
                <p className="font-medium">{result.appointment?.date}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t("time")}</p>
                <p className="font-medium">{result.appointment?.time}</p>
              </div>
            </div>
            {result.service && (
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 text-center text-muted-foreground">✂️</span>
                <div>
                  <p className="text-sm text-muted-foreground">{t("services")}</p>
                  <p className="font-medium">{result.service.name}</p>
                </div>
              </div>
            )}
            {result.staff && (
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t("staff")}</p>
                  <p className="font-medium">{result.staff.name}</p>
                </div>
              </div>
            )}
          </div>

          <p className="text-sm text-muted-foreground">
            {language === "it" 
              ? "Riceverai un promemoria prima dell'appuntamento."
              : "You'll receive a reminder before your appointment."
            }
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full glass-card rounded-2xl p-8 text-center animate-fade-in">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-6">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-serif font-medium mb-2">
            {language === "it" ? "Impossibile Confermare" : "Unable to Confirm"}
          </h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          
          <Link to="/">
            <Button className="rounded-full">
              {language === "it" ? "Torna alla Home" : "Back to Home"}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_saloneos-booking/artifacts/eysotq4t_smartbooker%20logo.png" 
              alt="SmartBooker" 
              className="h-8 w-auto"
            />
          </Link>
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-muted transition-colors"
          >
            {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 px-4 pb-8">
        <div className="max-w-md mx-auto">
          <div className="glass-card rounded-2xl p-8 text-center animate-fade-in">
            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
              <Calendar className="w-8 h-8 text-accent" />
            </div>
            
            <h1 className="text-2xl font-serif font-medium mb-2">
              {language === "it" ? "Conferma il tuo appuntamento" : "Confirm your appointment"}
            </h1>
            <p className="text-muted-foreground mb-8">
              {language === "it" 
                ? "Clicca il pulsante qui sotto per confermare la tua presenza."
                : "Click the button below to confirm your attendance."
              }
            </p>

            <Button
              onClick={handleConfirm}
              disabled={confirming}
              size="lg"
              className="w-full rounded-full btn-lift"
              data-testid="confirm-appointment-btn"
            >
              {confirming ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                  {language === "it" ? "Conferma in corso..." : "Confirming..."}
                </span>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  {language === "it" ? "Conferma Appuntamento" : "Confirm Appointment"}
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground mt-6">
              {language === "it" 
                ? "Se non puoi partecipare, contatta l'attività per cancellare o riprogrammare."
                : "If you can't attend, contact the salon to cancel or reschedule."
              }
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConfirmAppointment;
